import AddIcon from '@mui/icons-material/Add';
import { FormControlLabel, IconButton, Radio, RadioGroup, Stack, Switch } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  IInvoiceType,
  getAllInvoiceTypes,
  updateInvoiceType,
  updateInvoiceTypeDefault,
} from 'wisestep-api';
import { WsButton, WsDataTable, WsIconEdit } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { invoiceSettingsIndexOptions } from '../../invoicing/helper/InvocieSettingsHelper';

import AddEditInvoiceTypeDialog from './AddEditInvoiceTypeDialog';

export interface IAddEditModal {
  isOpen: boolean;
  data?: IInvoiceType;
}

const InvoiceType = () => {
  const [addEditInvoiceType, setAddEditInvoiceType] = useState<IAddEditModal>({ isOpen: false });
  const [invoiceTypes, setInvoiceTypes] = useState<IInvoiceType[]>([]);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];
  const setNotification = useNotificationStore((state) => state.setNotification);

  const getAll = () => {
    getAllInvoiceTypes().then(setInvoiceTypes);
  };

  useEffect(() => {
    getAll();
  }, []);

  const columns = [
    {
      Header: 'Invoice Type',
      accessor: 'name',
    },
    {
      Header: 'Start Sequence',
      accessor: 'sequenceStartFrom',
    },
    {
      Header: 'Active',
      accessor: 'active',
      Cell: (props) => {
        const row = props.row.original;
        return (
          <Switch
            defaultChecked={row.active}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateInvoiceType({ ...row, active: event.target.checked }).then(
                (updatedInvoiceType) => {
                  setInvoiceTypes(
                    invoiceTypes.map((item) =>
                      item.id === row.id ? updatedInvoiceType : item
                    ) as []
                  );
                }
              );
            }}
          />
        );
      },
    },
    {
      Header: 'Is Default',
      accessor: 'isDefault',
      Cell: ({ row: { original: row } }) => (
        <FormControlLabel sx={{ width: '100%' }} value={row.id} label="" control={<Radio />} />
      ),
    },
    {
      Header: 'Created By',
      accessor: 'createdByName',
    },
    {
      Header: '',
      accessor: 'edit',
      Cell: (props) => {
        const row = props.row.original;
        return (
          <IconButton onClick={() => setAddEditInvoiceType({ isOpen: true, data: row })}>
            <WsIconEdit />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <SettingsPageLayout
        title="Invoice Type"
        indexOptions={invoiceSettingsIndexOptions}
        breadCrumnbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.INVOICE,
          Pages['INVOICE_TYPE']
        )}
      >
        <Stack spacing={2} margin={2}>
          {manageAccess && (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
              <WsButton
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => setAddEditInvoiceType({ isOpen: true })}
              >
                Add Invoice Type
              </WsButton>
            </Stack>
          )}
          {invoiceTypes.length > 0 && (
            <RadioGroup
              value={invoiceTypes?.find((type) => type.default)?.id}
              onChange={async (e, v) => {
                try {
                  await updateInvoiceTypeDefault(v);
                  setNotification(success('Invoice Type has been updated successfully !!'));
                } catch {
                  setNotification(failed('Something went wrong , Please try again !!'));
                }
              }}
            >
              <WsDataTable columns={columns} data={invoiceTypes as []} />
            </RadioGroup>
          )}
        </Stack>
      </SettingsPageLayout>
      {addEditInvoiceType.isOpen && (
        <AddEditInvoiceTypeDialog
          onCancel={() => setAddEditInvoiceType({ isOpen: false })}
          onSave={() => {
            setAddEditInvoiceType({ isOpen: false });
            getAll();
          }}
          defaultValues={addEditInvoiceType.data}
        />
      )}
    </>
  );
};

export default InvoiceType;
